import { FC, useEffect } from 'react';
import { IonContent } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { Modal } from '../components/Modal';
import { BeeChatState } from './BeeChatState';
import { ActiveChatView } from './ActiveChatView';
import { ConversationListView } from './ConversationListView';
import { BeeChatViewModel } from './BeeChatViewModel';

const useStyles = createUseStyles({
    outerContainer: {
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden'
    },
    slidingContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        display: 'flex',
        width: '200%',
        height: '100%',
        transition: 'left 300ms ease-in-out'
    },
    showList: {
        left: 0
    },
    showChat: {
        left: '-100%'
    },
    slidePanel: {
        width: '50%',
        minWidth: '50%',
        height: '100%',
        flexShrink: 0,
        overflow: 'auto'
    }
});

export const BeeChatModal: FC = () => {
    const classes = useStyles();

    const vm = useInstance(BeeChatViewModel);
    useSubscription(() => vm);
    const isOpen = vm.isOpen();
    const isRestoringFromMinimize = vm.isRestoringFromMinimize();

    const chatState = useInstance(BeeChatState);
    useSubscription(() => chatState);

    const { currentView } = chatState.state;

    useEffect(() => {
        if (isOpen) {
            if (isRestoringFromMinimize) {
                // Restoring from minimize - don't reset state, just clear the flag
                vm.clearRestoringFlag();
            } else {
                // Fresh open - start a new conversation
                chatState.startNewConversation();
                // Load existing chats from API for the list
                chatState.loadChats();
            }

            // Send a caller-provided seed message (e.g. "tell me more about this insight")
            // only after the conversation state above has settled, so a fresh open's reset
            // can't wipe it. On restore-from-minimize it appends to the restored conversation.
            const initialMessage = vm.consumePendingInitialMessage();
            if (initialMessage) {
                chatState.sendMessage(initialMessage);
            }
        }
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleBack = () => {
        if (currentView === 'chat') {
            chatState.showConversationList();
        }
    };

    const containerClass = `${classes.slidingContainer} ${currentView === 'chat' ? classes.showChat : classes.showList}`;

    return (
        <Modal
            isOpen={isOpen}
            titleColor="secondary"
            buttonColor="light"
            title="Innerhive's BeeLine"
            data-id="bea-chat-modal"
            closeHandler={vm.close}
            minimizeHandler={vm.minimize}
            backHandler={currentView === 'chat' ? handleBack : undefined}
            maximizeDesktopHeight
        >
            <IonContent scrollY={false}>
                <div className={classes.outerContainer}>
                    <div className={containerClass}>
                        <div className={classes.slidePanel}>
                            <ConversationListView />
                        </div>
                        <div className={classes.slidePanel}>
                            <ActiveChatView />
                        </div>
                    </div>
                </div>
            </IonContent>
        </Modal>
    );
};
